import type { Ref } from 'vue'

export const useFavoritesStore = defineStore('favorites', () => {
  // Estado reactivo para los hoteles favoritos
  const favoriteHotels: Ref<Set<number>> = ref(new Set())
  const invalidateSearchCache = ref(false)

  // Configuración
  const appConfig = useAppConfig()
  const nuxtApp = useNuxtApp()
  const locale = nuxtApp.$i18n.locale
  const { loggedIn, user } = useUserSession()

  const userPending = computed(() => {
    return (user.value as OAuthUser)?.status?.id === 2
  })

  // Currency Store
  const currencyStore = useCurrencyStore()
  const { currency } = storeToRefs(currencyStore)

  // Función para agregar un hotel a favoritos
  const addFavorite = (hotelId: number) => {
    favoriteHotels.value.add(hotelId)
  }

  // Función para remover un hotel de favoritos
  const removeFavorite = (hotelId: number) => {
    favoriteHotels.value.delete(hotelId)
  }

  // Función para verificar si un hotel es favorito
  const isFavorite = (hotelId: number): boolean => {
    return favoriteHotels.value.has(hotelId) && !userPending.value
  }

  // Función para sincronizar el estado inicial de favoritos desde los datos del hotel
  const syncHotelFavorites = (hotels: Array<{ id: number; favorite?: boolean }> | null | undefined) => {
    if (!Array.isArray(hotels)) return
    for (const hotel of hotels) {
      if (hotel.favorite) {
        favoriteHotels.value.add(hotel.id)
      }
    }
  }

  // Función para limpiar todos los favoritos
  const clearFavorites = () => {
    favoriteHotels.value.clear()
  }

  // Watcher para limpiar favoritos cuando el usuario cierre sesión
  watch(loggedIn, (isLoggedIn) => {
    if (!isLoggedIn) {
      clearFavorites()
    }
  }, { immediate: true })

  // Función para marcar un hotel como favorito (con llamada a API)
  const favoriteHotel = async (hotelId: number): Promise<boolean> => {
    if (!loggedIn.value) {
      return false
    }

    addFavorite(hotelId)

    const { status } = await useFetch(`${useBaseUrl()}/favorites`, {
      method: 'POST',
      headers: {
        "Content-Type": "application/json",
        "User-token": useCookie('byhours_connect').value || ''
      },
      body: {
        hotel_id: hotelId,
        locale,
        channel_id: appConfig.channel_id,
        currency,
        email: (user.value as OAuthUser)?.email
      }
    })

    if (status.value !== 'success') {
      removeFavorite(hotelId)
      return false
    }

    invalidateSearchCache.value = true
    await refreshNuxtData('favorites')

    return true
  }

  // Función para desmarcar un hotel como favorito (con llamada a API)
  const unfavoriteHotel = async (hotelId: number): Promise<boolean> => {
    if (!loggedIn.value) {
      return false
    }

    removeFavorite(hotelId)

    const { status } = await useFetch(`${useBaseUrl()}/favorites-delete`, {
      method: 'POST',
      headers: {
        "Content-Type": "application/json",
        "User-token": useCookie('byhours_connect').value || ''
      },
      body: {
        hotel_id: hotelId,
        locale,
        channel_id: appConfig.channel_id,
        currency,
        email: (user.value as OAuthUser)?.email
      }
    })

    invalidateSearchCache.value = true

    if (status.value === 'success') {
      await refreshNuxtData('favorites')
      return true
    } else {
      addFavorite(hotelId)
      return false
    }
  }

  return {
    favoriteHotels,
    addFavorite,
    removeFavorite,
    isFavorite,
    syncHotelFavorites,
    clearFavorites,
    favoriteHotel,
    unfavoriteHotel,
    invalidateSearchCache
  }
}) 